/**
 * API Test Utilities
 * Helper functions to test API authentication and request configurations
 */

import { timeCapsuleApi } from '../services/apiService';

/**
 * Test authentication headers for different endpoints
 */
export function testAuthHeaders() {
    console.log('🧪 Testing API Authentication Headers');
    
    // Mock visitor ID in localStorage for testing
    localStorage.setItem('visitorId', 'test-visitor-id-12345');
    
    const testEndpoints = [
        '/visitors/signup',
        '/visitors/selfie',
        '/visitors/selfie-edited',
        '/events/track',
        '/zones-status/4.1',
        '/zones-status/4.1/claim',
        '/zones-status/4.1/payload',
        '/health'
    ];
    
    testEndpoints.forEach(endpoint => {
        // Access private method via type assertion (for testing only)
        const apiService = timeCapsuleApi as any;
        const authHeaders = apiService.getAuthHeaders(endpoint);
        const credentials = apiService.getCredentialsStrategy(endpoint);
        
        console.log(`📋 ${endpoint}:`, {
            authHeaders,
            credentials,
            needsAuth: Object.keys(authHeaders).length > 0
        });
    });
    
    // Clean up test data
    localStorage.removeItem('visitorId');
    
    console.log('✅ Authentication header test completed');
}

/**
 * Test API endpoints that should work without authentication
 */
export async function testPublicEndpoints() {
    console.log('🧪 Testing Public API Endpoints');
    
    try {
        // Test health endpoint
        console.log('Testing /health endpoint...');
        const healthResponse = await timeCapsuleApi.healthCheck();
        console.log('✅ Health check successful:', healthResponse);
        
        return { success: true, message: 'Public endpoints working' };
    } catch (error) {
        console.error('❌ Public endpoint test failed:', error);
        return { success: false, error };
    }
}

/**
 * Simulate visitor registration and test authenticated endpoints
 */
export async function testAuthenticatedFlow() {
    console.log('🧪 Testing Authenticated API Flow');
    
    try {
        // Test visitor signup (should work without auth)
        const signupData = {
            name: 'Test User',
            email: 'test@example.com',
            ageGroup: 'age_18_25' as const,
            nationality: 'Singapore',
            language: 'en'
        };
        
        console.log('Testing visitor signup...');
        const signupResponse = await timeCapsuleApi.signupVisitor(signupData);
        console.log('✅ Visitor signup successful:', signupResponse);
        
        // Test getting visitor data
        console.log('Testing get visitor data...');
        const visitorData = await timeCapsuleApi.getVisitor(signupResponse.visitorId);
        console.log('✅ Get visitor successful:', visitorData);
        
        return { 
            success: true, 
            visitorId: signupResponse.visitorId,
            message: 'Authenticated flow working' 
        };
    } catch (error) {
        console.error('❌ Authenticated flow test failed:', error);
        return { success: false, error };
    }
}

/**
 * Run all API tests
 */
export async function runAllApiTests() {
    console.log('🚀 Running All API Tests');
    
    // Test 1: Authentication headers
    testAuthHeaders();
    
    // Test 2: Public endpoints
    const publicTest = await testPublicEndpoints();
    
    // Test 3: Authenticated flow
    const authTest = await testAuthenticatedFlow();
    
    const summary = {
        publicEndpoints: publicTest.success,
        authenticatedFlow: authTest.success,
        visitorId: authTest.success ? (authTest as any).visitorId : null
    };
    
    console.log('📊 API Test Summary:', summary);
    return summary;
} 